import React, {PureComponent} from 'react';
import Box from "@mui/material/Box";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {Link as RouterLink} from "react-router-dom";
import Typography from "@mui/material/Typography";
import WorkoutCard from "../partials/WorkoutCard";
import Link from "@mui/material/Link";
import Skeleton from '@mui/material/Skeleton';
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import InfiniteScroll from "react-infinite-scroll-component";
import EventIcon from '@mui/icons-material/Event';
import CalendarModule from "./CalendarModule";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import LastWeekWorkoutsCard from "../partials/LastWeekWorkoutsCard";
import {withRouter} from "../../../hooks/withRouter";
import Button from "@mui/material/Button";
import GeneralStatsCard from "../partials/GeneralStatsCard";
import WorkoutStreakCard from "../partials/WorkoutStreakCard";
import GoalStatsCard from "../partials/GoalStatsCard";
import {UserContext} from "../../../core/UserContext";
import AiChatButton from "../../ai/AiChatButton";
import WeeklyDigestCard from "../../ai/WeeklyDigestCard";

class WorkoutsModule extends PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			workouts: UserLocalStorage.get(UrlBuilder.modules.workoutsModuleApi()) ?? null,
			isLoading: true,
			canFetchMoreWorkouts: false,
			isCalendarOpen: false,
		};
	}

	componentDidMount() {
		this.fetch();
	}

	fetch = async () => {
		this.setState({
			isLoading: true,
		});

		const response = await fetch(UrlBuilder.modules.workoutsModuleApi());

		if (response.status !== 200) {
			this.setState({
				isLoading: false,
			});
			return;
		}

		const responseData = await response.json();

		this.setState({
			workouts: responseData['data']['workouts'] ?? [],
			canFetchMoreWorkouts: responseData['data']['workouts'] && responseData['data']['workouts'].length >= 7, // Only stop when there is no more data.
			isLoading: false,
		});

		UserLocalStorage.set(UrlBuilder.modules.workoutsModuleApi(), responseData['data']['workouts'] ?? []);
	}

	fetchMore = async () => {
		const response = await fetch(UrlBuilder.modules.workoutsModuleApi(this.state.workouts.length));

		if (response.status !== 200) {
			return;
		}
		
		const responseData = await response.json();

		this.setState({
			workouts: this.state.workouts.concat(responseData['data']['workouts'] ?? []),
			canFetchMoreWorkouts: responseData['data']['workouts'] && responseData['data']['workouts'].length >= 7, // Only stop when there is no more data.
		});
	}
	
	toggleCalendar = (open) => {
		this.setState({
			isCalendarOpen: open,
		});
	}

	hasNeverBeenFetched = () => {
		return this.state.workouts === null;
	}

	renderNoWorkouts = () => {
		return (
			<Box sx={{
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 3,
				bgcolor: 'background.paper',
				p: { xs: 3, sm: 4 },
				textAlign: 'center',
				maxWidth: 480,
				mx: 'auto',
			}}>
				<Box
					component={RouterLink}
					to={UrlBuilder.routine.routineOverviewPage(true)}
					sx={{
						width: 56, height: 56, borderRadius: '50%',
						bgcolor: (theme) => theme.palette.mode === 'dark'
							? 'rgba(245,0,87,0.12)' : 'rgba(245,0,87,0.06)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						mx: 'auto', mb: 2,
						cursor: 'pointer',
						transition: 'background-color 0.2s',
						'&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark'
							? 'rgba(245,0,87,0.2)' : 'rgba(245,0,87,0.1)' },
					}}
				>
					<AddToPhotosIcon sx={{ fontSize: 28, color: 'secondary.main' }} />
				</Box>

				<Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
					No workouts yet
				</Typography>
				<Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
					Create a routine with your exercises, then start your first workout from there.
				</Typography>

				<Button
					component={RouterLink}
					to={UrlBuilder.routine.routineOverviewPage(true)}
					variant="contained"
					color="secondary"
					size="large"
					startIcon={<AddToPhotosIcon />}
					sx={{ px: 3, textTransform: 'none', fontWeight: 600 }}
				>
					Create a Routine
				</Button>

				<Box sx={{ mt: 2 }}>
					<Link
						component={RouterLink}
						to={UrlBuilder.exercise.exercisesOverviewPage()}
						sx={{ fontSize: 13, color: 'text.secondary' }}
					>
						or browse exercises first
					</Link>
				</Box>
			</Box>
		);
	}
	
	renderWorkoutCardSkeleton = () => (
		<Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, px: 2, py: 1.5 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
				<Skeleton variant="circular" width={36} height={36} />
				<Box sx={{ flex: 1 }}>
					<Skeleton variant="text" width="55%" sx={{ fontSize: 16 }} />
					<Skeleton variant="text" width="35%" sx={{ fontSize: 12 }} />
				</Box>
			</Box>
			<Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
				{[1, 2, 3].map(i => (
					<Skeleton key={i} variant="rounded" width={70 + i * 10} height={22} sx={{ borderRadius: 1 }} />
				))}
			</Box>
			<Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5, display: 'flex', justifyContent: 'space-between' }}>
				<Skeleton variant="text" width={120} sx={{ fontSize: 12 }} />
				<Skeleton variant="text" width={40} sx={{ fontSize: 12 }} />
			</Box>
		</Box>
	)

	renderLoading = () => {
		return <>
			<Skeleton variant="rounded" height={80} sx={{ borderRadius: 2.5, mb: 2.5 }} />

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
				{[1, 2, 3].map(i => (
					<React.Fragment key={i}>
						{this.renderWorkoutCardSkeleton()}
					</React.Fragment>
				))}
			</Box>
		</>;
	}

	render() {
		if (this.hasNeverBeenFetched()) {
			return this.renderLoading();
		}

		return <>
			<WorkoutStreakCard/>

			<UserContext.Consumer>
				{({user}) => (
					user.features?.includes('ai_features') &&
						<WeeklyDigestCard />
				)}
			</UserContext.Consumer>

			<Box paddingTop={ 2.5 } />

			<LastWeekWorkoutsCard workouts={this.state.workouts}/>

			<Box paddingTop={ 2.5 } />

			<GeneralStatsCard/>

			<Box paddingTop={ 1.5 } />

			<GoalStatsCard/>

			<Box paddingTop={ 2.5 } />

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<Typography variant="h5" sx={{ fontSize: 21 }}>
					Workout History
				</Typography>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
					<UserContext.Consumer>
						{({user, setUser}) => (
							user.features?.includes('ai_features') &&
							<AiChatButton
								apiUrl={UrlBuilder.ai.summarize()}
								defaultActionLabel="Summarize my training"
								presetSuggestions={[
									"Which exercises am I improving on?",
									"How was my last workout?",
									"Analyze my performance this week",
									"Which muscles need more focus?",
									"Which lifts are lagging behind?",
									"Am I training often enough?",
									"How am I tracking against my goals?",
									"Which exercises have the most volume?",
									"Where have I plateaued recently?",
									"Do I have any recent PRs?",
									"Suggest a focus area for next month",
									"Am I balancing push and pull work?",
								]}
							/>
						)}
					</UserContext.Consumer>

					<Button
						variant="contained"
						onClick={() => this.toggleCalendar(true)}
						color="primary"
					>
						<EventIcon />
					</Button>
				</Box>
			</Box>

			<CalendarModule isOpen={this.state.isCalendarOpen} closeSelfFunc={() => this.toggleCalendar(false)}/>

			{this.state.isLoading && (
				<LinearProgress sx={{ mt: 0.5, borderRadius: 1, bgcolor: 'transparent' }} />
			)}

			<Box sx={{ mt: 1.5 }} />

			{this.state.workouts.length === 0 ?
				this.renderNoWorkouts()
				:
				<InfiniteScroll
					dataLength={this.state.workouts.length}
					next={this.fetchMore}
					hasMore={this.state.canFetchMoreWorkouts}
					loader={<Box display="flex" justifyContent="center" alignItems="center" minHeight="10vh">
						<CircularProgress color="primary" />
					</Box>}
					style={{overflowX: 'hidden' }}
				>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						{this.state.workouts.map(workout =>
							<WorkoutCard key={workout.id} workout={workout} />
						)}
					</Box>
				</InfiniteScroll>
			}
		</>;
	}
}
export default withRouter(WorkoutsModule)